export function findBaseModelOption (options, option) {
  if (!option || !Array.isArray(options)) {
    return null
  }
  const name = String(option.name || '')
  const baseName = name.replace(/\.(\d+)(?=\s|\(|$)/, '')
  if (!baseName || baseName === name) {
    return null
  }
  return options.find((item) => item && item !== option && item.name === baseName) || null
}

export function heightLimitOption (options, option) {
  const base = findBaseModelOption(options, option)
  if (!base?.dimension?.height || !Array.isArray(base.special_dim) || base.special_dim.length === 0) {
    return option
  }
  return base
}

export function dimensionRangeValues (range, maxOverride = 0) {
  if (!range) {
    return []
  }
  const min = Number.parseInt(range.min, 10)
  const step = Number.parseInt(range.step, 10) || 1
  let max = Number.parseInt(range.max, 10)
  const cap = Number.parseInt(maxOverride, 10)
  if (cap > 0) {
    max = cap
  }
  if (!Number.isFinite(min) || !Number.isFinite(max) || step <= 0 || max < min) {
    return []
  }
  const values = []
  for (let value = min; value <= max; value += step) {
    values.push(value)
  }
  return values
}

export function buildHeightValues (configurator) {
  const modelStep = configurator?.steps?.find(step => step.model_step)
  const option = modelStep?.options?.find(item => item.selected)
  if (!option?.dimension?.height) {
    return []
  }
  const dimensionStep = typeof configurator.getDimensionStep === 'function'
    ? configurator.getDimensionStep()
    : null
  const width = dimensionStep?.width?.value ?? option.dimension?.width?.value
  const source = heightLimitOption(modelStep.options, option) || option
  const heightMax = computeHeightMaxFromSpecialDim(source.special_dim, width)
  return dimensionRangeValues(source.dimension?.height || option.dimension.height, heightMax)
}

export function buildWidthValues (configurator) {
  const modelStep = configurator?.steps?.find(step => step.model_step)
  const option = modelStep?.options?.find(item => item.selected)
  const dimensionStep = typeof configurator.getDimensionStep === 'function'
    ? configurator.getDimensionStep()
    : configurator?.steps?.find(step => step.type === 'dimension')
  const range = option?.dimension?.width || dimensionStep?.width
  return {
    values: dimensionRangeValues(range),
    unit: range?.unit || 'cm',
    current: Number.parseInt(dimensionStep?.width?.value ?? option?.dimension?.width?.value, 10)
  }
}

export function sharedGateHeightValues (gates) {
  const lists = (gates || []).map(buildHeightValues).filter(list => list.length)
  if (lists.length === 0) {
    return []
  }
  const shared = lists.reduce((current, list) => current.filter(value => list.includes(value)))
  return shared.length ? shared : lists[0]
}

export function nearestAllowedValue (values, current) {
  const parsed = Number.parseInt(current, 10)
  if (!Array.isArray(values) || values.length === 0 || !Number.isFinite(parsed)) {
    return parsed
  }
  if (values.includes(parsed)) {
    return parsed
  }
  const lower = values.filter(value => value <= parsed)
  return lower.length ? lower[lower.length - 1] : values[values.length - 1]
}

export function computeHeightMaxFromSpecialDim (specialDim, widthValue) {
  const width = Number.parseInt(widthValue, 10)
  if (!Array.isArray(specialDim) || !Number.isFinite(width)) {
    return 0
  }

  const allowed = specialDim
    .map((row) => {
      if (!row || !row.width || !row.height) {
        return null
      }
      const widthFrom = Number.parseInt(row.width.from, 10)
      const widthTo = Number.parseInt(row.width.to, 10)
      const heightTo = Number.parseInt(row.height.to, 10)
      const price = Number.parseFloat(row.price)
      if (!Number.isFinite(widthFrom) || !Number.isFinite(widthTo) || !Number.isFinite(heightTo)) {
        return null
      }
      if (price <= 0) {
        return null
      }
      if (widthFrom <= width && widthTo >= width) {
        return heightTo
      }
      return null
    })
    .filter((value) => value !== null)

  if (allowed.length === 0) {
    return 0
  }

  return Math.max(...allowed)
}

export function clampDimensionValue (value, min, max, step) {
  const parsed = Number.parseInt(value, 10)
  const minValue = Number.parseInt(min, 10)
  const maxValue = Number.parseInt(max, 10)
  const stepValue = Number.parseInt(step, 10) || 1
  if (!Number.isFinite(parsed) || !Number.isFinite(minValue) || !Number.isFinite(maxValue)) {
    return parsed
  }
  let next = parsed
  if (next > maxValue) {
    next = maxValue
  }
  if (next < minValue) {
    next = minValue
  }
  const offset = next - minValue
  next = minValue + Math.floor(offset / stepValue) * stepValue
  if (next > maxValue) {
    next = maxValue
  }
  return next
}
